import type { MouseEvent } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { AvatarInitials } from '@/components/ui/avatar-initials'
import { statusPill, priorityLabels, priorityVariants } from '@/lib/status'
import { isColleagueViewer } from '@/lib/scope'
import { formatTicketRef } from '@/lib/ticketRef'
import { AgentQueuePill } from '@/features/agent-channel/ClaimBanner'
import type { Ticket, ViewerRelation } from '@/types/ticket'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

// V2 table row (mockup's Tickets inbox `<tr>`): 44px cells, hairline rules
// from the tbody's divide-y, hover wash via `hover:bg-etn-row-hover`. Clicking
// anywhere in the row navigates, but the subject itself is a real `<a>` so
// keyboard/screen-reader users and cmd-click/open-in-new-tab keep working —
// the row's onClick bails out when the click already landed on a nested
// link/button so that element's own navigation isn't double-fired.
export function TicketRow({
  ticket,
  relation,
  hrefBase = '/tickets',
  asUser,
  companyName,
}: {
  ticket: Ticket
  relation: ViewerRelation
  hrefBase?: string
  asUser?: string
  /** Present only for the admin queue, which adds Company and Assigned to columns. */
  companyName?: string
}) {
  const queue = companyName !== undefined
  const pill = statusPill(ticket, queue ? 'staff' : 'customer')
  const navigate = useNavigate()
  const reporterName = ticket.creator?.name ?? ticket.creator?.email ?? null
  const to = `${hrefBase}/${ticket._id}`
  const search = { asUser }

  const goToTicket = (e: MouseEvent<HTMLTableRowElement>) => {
    if (e.target instanceof Element && e.target.closest('a, button')) return
    navigate({ to: to as never, search: search as never })
  }

  return (
    <tr onClick={goToTicket} className="cursor-pointer text-etn-cell transition-colors hover:bg-etn-row-hover">
      {/* w-full with max-w-0 gives the subject whatever width the other columns leave, so a long subject truncates
          instead of pushing the last column off the card. Only the subject itself truncates; the ref and pills stay. */}
      <td className="h-11 w-full max-w-0 px-4 align-middle font-medium text-etn-text">
        <div className="flex min-w-0 items-center gap-2">
          <Link to={to as never} search={search as never} className="truncate hover:underline" title={ticket.subject}>
            {ticket.subject}
          </Link>
          {ticket.viewerFollows ? (
            <span className="shrink-0 rounded-etn-menu bg-etn-neutral-subtle px-1.5 py-px text-etn-micro font-normal text-etn-neutral-text">
              Following
            </span>
          ) : isColleagueViewer(relation) && (
            <span className="shrink-0 rounded-etn-menu bg-etn-neutral-subtle px-1.5 py-px text-etn-micro font-normal text-etn-neutral-text">
              Colleague&#39;s
            </span>
          )}
          <small className="shrink-0 font-normal text-etn-micro text-etn-text-soft">{formatTicketRef(ticket)}</small>
          {queue && ticket.agentClaim && <AgentQueuePill claim={ticket.agentClaim} />}
        </div>
      </td>
      <td className="h-11 max-w-[180px] truncate px-4 align-middle">
        {reporterName && queue && <span className="font-medium text-etn-text-cell">{reporterName}</span>}
        {reporterName && !queue && (
          <span className="flex items-center gap-2">
            <AvatarInitials name={reporterName} size="sm" className="h-[26px] w-[26px] shrink-0 text-etn-micro" />
            <span className="truncate font-medium text-etn-text-cell">{reporterName}</span>
          </span>
        )}
      </td>
      {queue && (
        <>
          <td className="h-11 max-w-[160px] truncate px-4 align-middle font-normal text-etn-text-soft">{companyName}</td>
          <td className="h-11 max-w-[160px] truncate px-4 align-middle">
            {ticket.assignee?.name
              ? <span className="text-etn-text-cell">{ticket.assignee.name}</span>
              : <span className="text-etn-text-soft">Unassigned</span>}
          </td>
        </>
      )}
      <td className="h-11 whitespace-nowrap px-4 align-middle">
        <Badge variant={pill.variant}>{pill.label}</Badge>
      </td>
      <td className="h-11 whitespace-nowrap px-4 align-middle">
        <Badge variant={priorityVariants[ticket.priority]}>{priorityLabels[ticket.priority]}</Badge>
      </td>
      <td className="h-11 whitespace-nowrap px-4 align-middle font-normal text-etn-text-soft">
        {dayjs(ticket.lastMessageAt).fromNow(true)}
      </td>
    </tr>
  )
}
